import type { FetchMenuTopRowProps, SmartSort } from '@iheartjane/dm-sdk';
import type { SmartSortRotatorSnapshot } from '@iheartjane/utils';
import { SmartSortRotator } from '@iheartjane/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AlgoliaProduct } from '@jane/search/types';
import { useSDK } from '@jane/shared-ecomm/providers';
import type { MenuCategoryPath } from '@jane/shared/types';

import { trackUnexpectedError } from '../mixpanelEvents';

const STOP_AFTER_CONSECUTIVE_ERRORS = 1;
const MAX_ERROR_REPORTS_PER_STORE = 3;

// Separate reasons because the gate and the hover handlers suspend the same
// rotator independently; with one reason, whichever ran last would cancel the other.
const GATE = 'gate';
const HOVER = 'hover';

type UseMenuTopRowProps = {
  enabled: boolean;
  enableRefetch?: boolean;
  initialRow?: SmartSort<AlgoliaProduct>;
  /**
   * Not sent to the ad server, but a change means the shopper moved elsewhere in
   * the menu and a fresh decision is due. `searchFilter` alone misses the
   * all↔featured move, where neither page contributes a category filter.
   */
  pageCategory?: MenuCategoryPath;
  refreshTime: number;
} & Omit<FetchMenuTopRowProps, 'excludeCreativeIds' | 'searchAttributes'>;

/**
 * Leaves an unfilled rotator alone so its initial fetch is never cancelled. The
 * row must come off the rotator, not the hook snapshot — that snapshot retains the
 * previous row across a swap, so it reports a brand-new rotator as already filled.
 */
const applyGate = (
  rotator: SmartSortRotator<SmartSort<AlgoliaProduct>>,
  enableRefetch: boolean
): void => {
  if (rotator.getSnapshot().row === undefined) {
    return;
  }
  if (enableRefetch) {
    rotator.resume(GATE);
  } else {
    rotator.pause(GATE);
  }
};

interface UseMenuTopRowResponse {
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  pauseRefetching: () => void;
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  unpauseRefetching: () => void;
}

/**
 * Rotates the sponsored top-of-menu row on an interval. The rotation loop lives in
 * {@link SmartSortRotator}; this hook only wires it to React and supplies the DM
 * SDK as transport. `fetchMenuTopRow` takes no AbortSignal, so an in-flight request
 * is never truly cancelled — the rotator discards the stale result instead.
 */
export const useMenuTopRowWithRefresh = ({
  enableRefetch = true,
  enabled,
  initialRow,
  pageCategory,
  refreshTime,
  searchFilter,
  storeId,
}: UseMenuTopRowProps): UseMenuTopRowResponse => {
  const { sdk } = useSDK();

  const enableRefetchRef = useRef(enableRefetch);
  useEffect(() => {
    enableRefetchRef.current = enableRefetch;
  });

  const intervalMs = refreshTime * 1000;

  const [snapshot, setSnapshot] = useState<
    SmartSortRotatorSnapshot<SmartSort<AlgoliaProduct>>
  >({ isError: false, isFetching: false });

  const rotatorRef = useRef<SmartSortRotator<SmartSort<AlgoliaProduct>> | null>(
    null
  );

  // The rotator's own cap spans one instance, and every filter or category change
  // builds a new one — so a store with a dead ad server re-reported on each
  // navigation. Count across instances, resetting on success to stay "consecutive".
  const reportedErrorsRef = useRef(0);
  useEffect(() => {
    reportedErrorsRef.current = 0;
  }, [storeId]);

  useEffect(() => {
    if (!enabled || searchFilter === undefined || !sdk) {
      rotatorRef.current = null;
      setSnapshot({ isError: false, isFetching: false, row: undefined });
      return;
    }

    const rotator = new SmartSortRotator<SmartSort<AlgoliaProduct>>({
      fetchNextSet: async ({ current }) =>
        sdk.fetchMenuTopRow<
          AlgoliaProduct,
          { excludeCreativeIds: number[] | undefined }
        >({
          excludeCreativeIds: current?.['creativeIds'],
          searchAttributes: ['*'],
          searchFilter,
          storeId,
        }),
      hasItems: (row) => row.products.length > 0,
      intervalMs,
      maxConsecutiveErrors: STOP_AFTER_CONSECUTIVE_ERRORS,
      onError: (error) => {
        if (reportedErrorsRef.current >= MAX_ERROR_REPORTS_PER_STORE) {
          return;
        }
        reportedErrorsRef.current += 1;
        trackUnexpectedError({ error });
      },
      stopOnEmpty: true,
      row: initialRow,
    });

    rotatorRef.current = rotator;
    const unsubscribe = rotator.subscribe(() => {
      applyGate(rotator, enableRefetchRef.current);
      const next = rotator.getSnapshot();
      if (next.row !== undefined && !next.isFetching && !next.isError) {
        reportedErrorsRef.current = 0;
      }
      setSnapshot((prev) => {
        const row = next.isFetching ? (next.row ?? prev.row) : next.row;
        if (
          prev.isError === next.isError &&
          prev.isFetching === next.isFetching &&
          prev.row === row
        ) {
          return prev;
        }
        return { isError: next.isError, isFetching: next.isFetching, row };
      });
    });

    rotator.start();

    if (initialRow) {
      setSnapshot((prev) => {
        return { ...prev, row: initialRow };
      });
    }

    return () => {
      rotator.destroy();
      unsubscribe();
      rotatorRef.current = null;
    };
  }, [
    enabled,
    initialRow,
    searchFilter,
    storeId,
    intervalMs,
    sdk,
    pageCategory,
  ]);

  useEffect(() => {
    if (rotatorRef.current) {
      applyGate(rotatorRef.current, enableRefetch);
    }
  }, [enableRefetch]);

  const pauseRefetching = useCallback(
    () => rotatorRef.current?.pause(HOVER),
    []
  );
  const unpauseRefetching = useCallback(
    () => rotatorRef.current?.resume(HOVER),
    []
  );

  return {
    isError: snapshot.isError,
    isFetching: snapshot.isFetching,
    isSuccess: !!snapshot.row && !snapshot.isError && !snapshot.isFetching,
    pauseRefetching,
    rowInstance: snapshot.row,
    unpauseRefetching,
  };
};
